import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { useNavigationGuard } from '../../../../../hooks/useNavigationGuard';
import * as actions from "../../../../../redux/actions/audioEngineer";
import musicLoader from '../../../../../assets/images/music-loader.gif';
import loaderBackground from '../../../../../assets/images/Background+Blur.svg';
import { trackNavigationEvent, createTrackingPayload } from '../../../../../helper/navigationTracker';
import { completeProcess } from '../../../../../redux/actions/backgroundProcesses';

const messages = [
  "Analyzing your music…",
  "Detecting genre…",
  "Detecting sub-genre…",
  "Building custom plugin chain…",
  "Adapting engine to style…",
  "Optimizing stereo field…",
  "Adjusting frequency balance…",
  "Optimizing loudness…",
  "Applying magic touch…",
  "Preparing your preview…",
  "Almost there…"
];

const leaveWarningMessage = "Your processing is still in progress. Leaving this page may interrupt the task.";

const ProcessingLoader = ({ isMaster, taskId, uploadId, isDemo = false }) => {
  const dispatch = useDispatch()
  const [currentMessage, setCurrentMessage] = useState(0);
  const [progress, setProgress] = useState(0);
  const { currentProcessId } = useSelector((state) => state.backgroundProcesses || {});

  // 1. Rotate messages/progress
  useEffect(() => {
    const stepCount = messages.length - 1; // last message is 100%
    const interval = setInterval(() => {
      setCurrentMessage(prev => {
        if (prev < stepCount) {
          const next = prev + 1;
          setProgress(Math.round((next / stepCount) * 100));
          return next;
        } else {
          clearInterval(interval);
          return prev;
        }
      });
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  // 2. Block browser back/forward and app back navigation only
  useNavigationGuard(
    !isDemo,
    `${leaveWarningMessage} Do you want to leave?`,
    () => {
      dispatch(actions.clearUploadData());
      dispatch(actions.uploadData({}))
      dispatch(actions.previewData({}))
      dispatch(actions.taskMetaData({}))
      dispatch(completeProcess(currentProcessId));
    }
  );

  // 3. Warn on browser refresh/close/tab close
  useEffect(() => {
    if (isDemo) return;
    const handleBeforeUnload = (event) => {
      const payload = createTrackingPayload('page_refresh', {
        taskId: taskId,
        uploadId: uploadId,
        operationName: isMaster ? 'AudioEngineer Mastering' : 'AudioEngineer Preview',
        errorMessage: `User attempted to refresh/close the page during AudioEngineer ${isMaster ? 'mastering' : 'preview'}`,
        metadata: {
          component: 'AudioEngineer ProcessingLoader',
          isMaster,
        }
      });
      trackNavigationEvent(payload);

      event.preventDefault();
      event.returnValue = leaveWarningMessage;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isDemo, isMaster, taskId, uploadId]);

  return (
    <div className="bg-white max-w-[700px] mx-auto flex flex-col items-center justify-center text-center gap-6 rounded-2xl p-4 lg:p-5">
      <div className="relative w-48 h-48 mx-auto">
        {/* Background image */}
        {/* <img
          src={loaderBackground}
          className="absolute inset-0 w-full h-full object-contain"
        /> */}

        {/* Foreground / animated image */}
        <img
          src={musicLoader}
          alt="Processing"
          className="absolute inset-0 w-full h-full object-contain"
        />
      </div>
      <div className="w-full max-w-[400px] mx-auto">
        <div className="bg-[#00067e] h-2 rounded-full transition-all duration-500 mb-3" style={{ width: `${progress}%` }} />
        <div className="text-[#FA6138] text-base font-medium">{messages[currentMessage]}</div>
      </div>
      <div className="text-theme_clr2 text-base font-medium opacity-80 leading-relaxed">
        {isMaster
          ? <i>Getting your song ready for release.</i>
          : <i>
            We're analyzing your audio and building a custom<br />
            mastering chain based on its unique genre and tone.
          </i>
        }
      </div>
      <div className="text-sm font-semibold text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
        Please do not refresh, close, or navigate away while processing.
      </div>
    </div>
  );
};

export default ProcessingLoader;
